/**
 * Anrufphase für die Anrufleiste (ActiveCallBar).
 *
 * Kundenfeedback Kanzlei Polz 27.10.2026 (I.4): „Wenn man über KARS jemanden
 * anruft, kommt kein Wählton. Man weiß dann immer nicht, ob der Anruf durch
 * geht oder nicht.“ Die Leiste zeigt deshalb sichtbar „Wird verbunden …“ →
 * „Es klingelt …“ → „Verbunden 00:12“ → „Besetzt / Nicht erreicht / Beendet“.
 *
 * Im Callback-Modus gibt es zwei Twilio-Beine:
 *   - `status`       – das eigene Telefon des Nutzers (Twilio ruft zuerst ihn an)
 *   - `calleeStatus` – der angerufene Kontakt (<Dial><Number statusCallback>)
 * Maßgeblich für „klingelt / verbunden / besetzt“ ist das Kontakt-Bein; das
 * Nutzer-Bein bestimmt nur die Phase davor („Ihr Telefon klingelt“).
 */
import { gtKey } from "@/i18n/gt";

export type CallPhase =
  | "connecting"
  | "own-phone-ringing"
  | "own-phone-missed"
  | "dialing"
  | "ringing"
  | "connected"
  | "busy"
  | "no-answer"
  | "failed"
  | "canceled"
  | "ended";

export interface CallPhaseInput {
  mode?: string | null;
  status?: string | null;
  calleeStatus?: string | null;
}

const TERMINAL_PHASES: ReadonlySet<CallPhase> = new Set<CallPhase>([
  "own-phone-missed",
  "busy",
  "no-answer",
  "failed",
  "canceled",
  "ended",
]);

/** Deutsche Quelltexte – Anzeige im Template über `$gt(label)`. */
export const CALL_PHASE_LABELS: Readonly<Record<CallPhase, string>> = {
  connecting: gtKey("Wird verbunden …"),
  "own-phone-ringing": gtKey("Ihr Telefon klingelt – bitte abheben"),
  "own-phone-missed": gtKey("Ihr Telefon wurde nicht abgenommen"),
  dialing: gtKey("Wird verbunden …"),
  ringing: gtKey("Es klingelt …"),
  connected: gtKey("Verbunden"),
  busy: gtKey("Besetzt"),
  "no-answer": gtKey("Nicht erreicht"),
  failed: gtKey("Verbindung fehlgeschlagen"),
  canceled: gtKey("Abgebrochen"),
  ended: gtKey("Beendet"),
};

/** Ergebnis des Kontakt-Beins, sobald gewählt wurde. */
function phaseFromCallee(calleeStatus: string, ownLegEnded: boolean): CallPhase {
  switch (calleeStatus) {
    case "queued":
    case "initiated":
      return ownLegEnded ? "canceled" : "dialing";
    case "ringing":
      return ownLegEnded ? "canceled" : "ringing";
    case "in-progress":
      return ownLegEnded ? "ended" : "connected";
    case "busy":
      return "busy";
    case "no-answer":
      return "no-answer";
    case "failed":
      return "failed";
    case "canceled":
      return "canceled";
    case "completed":
      return "ended";
    default:
      return ownLegEnded ? "ended" : "dialing";
  }
}

/** Phase vor dem Wählen bzw. für Modi mit nur einem Bein (Browser/API). */
function phaseFromOwnLeg(status: string, isCallback: boolean): CallPhase {
  switch (status) {
    case "ringing":
      return isCallback ? "own-phone-ringing" : "ringing";
    case "in-progress":
      // Callback: Nutzer hat abgehoben, Twilio wählt jetzt den Kontakt.
      return isCallback ? "dialing" : "connected";
    case "busy":
      return isCallback ? "own-phone-missed" : "busy";
    case "no-answer":
      return isCallback ? "own-phone-missed" : "no-answer";
    case "failed":
      return "failed";
    case "canceled":
      return "canceled";
    case "completed":
      return "ended";
    default:
      return "connecting";
  }
}

const OWN_LEG_TERMINAL = new Set(["completed", "busy", "no-answer", "failed", "canceled"]);

export function deriveCallPhase(call: CallPhaseInput): CallPhase {
  const status = (call.status ?? "").trim();
  const calleeStatus = (call.calleeStatus ?? "").trim();
  const isCallback = (call.mode ?? "callback") === "callback";
  if (calleeStatus) {
    return phaseFromCallee(calleeStatus, OWN_LEG_TERMINAL.has(status));
  }
  return phaseFromOwnLeg(status, isCallback);
}

export function isTerminalCallPhase(phase: CallPhase): boolean {
  return TERMINAL_PHASES.has(phase);
}

/**
 * Hinweis „Ihr Telefon klingelt zuerst“ nur im Callback-Modus und nur,
 * solange der Nutzer noch nicht abgehoben hat.
 */
export function showsOwnPhoneHint(call: CallPhaseInput, phase: CallPhase): boolean {
  const isCallback = (call.mode ?? "callback") === "callback";
  return isCallback && (phase === "connecting" || phase === "own-phone-ringing");
}

/** mm:ss für die Gesprächsdauer. */
export function formatCallDuration(ms: number): string {
  const secs = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}
